'use client';

import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { lobbyHrefForBoard } from '@/lib/lobby-routes';
import type { MockBoard } from '../data/mock-boards';
import { formatBoardFloor, isFloorPending } from '../data/mock-boards';
import { BoardTooltip } from './BoardTooltip';
import { LiveDot } from '../ui/LiveDot';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface BoardroomCardProps {
  board: MockBoard;
  active?: boolean;
  onSelect?: () => void;
  /**
   * True while a tap-driven nav to this card is in flight. Renders a
   * darkened spinner overlay so a slow nav doesn't read as an
   * unresponsive tap. Cleared by `LobbyClient.setActiveBoardId` once
   * `activeBoardId` resolves to this card's id.
   */
  pending?: boolean;
}

export function BoardroomCard({
  board,
  active,
  onSelect,
  pending,
}: BoardroomCardProps) {
  const router = useRouter();
  // `taken` carries "tax-paying (non-creator) seat count" semantics —
  // see `gridSummaryToMockBoard`. A full house means every non-creator
  // seat is claimed, which is the "everything's live" state worth
  // bragging about with an emerald highlight.
  const seatsFilled = board.capacity > 0 && board.taken >= board.capacity;
  return (
    <Tooltip delayDuration={200} disableTouch>
      <TooltipTrigger asChild>
        <div className="relative w-full">
          <button
            type="button"
            onClick={onSelect}
            aria-current={active ? 'true' : undefined}
            onPointerEnter={() => router.prefetch(lobbyHrefForBoard(board.id))}
            className={cn(
              // Mirrors `BoardCard`: the active card stays clickable so a
              // tap from the lobby overlay drops `?mode=lobby` and
              // returns the viewer to the same board (instead of being
              // a silent no-op through `disabled={active}`).
              'relative w-full flex flex-col p-2 rounded-[5px] text-left',
              'bg-gradient-to-b from-[#1a0508]/95 via-[#0a0305]/95 to-[#1a0508]/95',
              'border backdrop-blur-sm group overflow-hidden',
              active ? 'border-red-400/70' : 'border-red-500/40 hover:border-red-400/70',
              'shadow-[0_20px_40px_-20px_rgba(239,68,68,0.35)]',
            )}
          >
            {/* Rotating gilded rim */}
            <span
              aria-hidden
              className="pointer-events-none absolute -inset-[1px] rounded-[5px] opacity-60"
              style={{
                background:
                  'conic-gradient(from 0deg, rgba(239,68,68,0.6), rgba(255,215,120,0.4), rgba(239,68,68,0.6), rgba(127,29,29,0.2), rgba(239,68,68,0.6))',
                animation: 'lobby-rim-rotate 6s linear infinite',
                WebkitMask:
                  'linear-gradient(#000, #000) content-box, linear-gradient(#000, #000)',
                WebkitMaskComposite: 'xor',
                padding: 1,
              }}
            />

            {/* Scanline overlay */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(0deg, transparent 0px, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 3px)',
              }}
            />

            <div className="relative flex items-center gap-2 mb-1.5">
              <div
                className={cn(
                  'relative w-7 h-7 rounded-[3px] overflow-hidden',
                  'border border-red-500/60 shadow-[inset_0_0_8px_rgba(239,68,68,0.6)]',
                )}
              >
                {/* Canonical Boardroom mark — pixel-art portrait. Mirrors
                 *  the asset returned by `resolveGridDisplay()` for HV grid 0
                 *  so this avatar, every `BoardCover` consumer, and the
                 *  `BoardroomIcon` component all read as the same identity. */}
                <Image
                  src="/boardroom.png"
                  alt=""
                  fill
                  sizes="28px"
                  className="object-cover"
                  style={{ imageRendering: 'pixelated' }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-bold text-amber-100 truncate leading-tight">
                  {board.name}
                </div>
                <div className="text-[9px] tracking-[0.12em] font-mono text-red-300/80 truncate leading-tight mt-0.5">
                  Takeover&rsquo;s top level board
                </div>
              </div>
              <LiveDot color="red" size={7} />
            </div>

            <div className="relative mt-0.5 space-y-1.5">
              {/* The 7D Rewards stat block was removed in favour of
                  the floor price — rewards are biased toward the
                  yield-flowing slice of the protocol and missed
                  chat-tax-driven success entirely. Floor is a
                  universally-applicable signal (every board has a
                  floor, regardless of yield model) and matches the
                  community card style for visual consistency. The
                  surrounding panel + label sizing stays unchanged
                  so the boardroom card's footprint doesn't shift
                  the lobby grid layout. */}
              <div className="px-1.5 py-1 rounded-[3px] bg-black/40 border border-red-500/20">
                <div className="text-[8px] tracking-[0.18em] font-mono uppercase text-zinc-500">
                  Floor
                </div>
                {/* While the TAKEOVER/ETH oracle hasn't resolved a USD
                    figure we render a skeleton bar instead of the raw
                    token amount. The previous fallback (`24801.3k
                    TAKEOVER`) collided with `formatBoardFloor`'s
                    defensive `$`-prefix and produced the broken
                    `$24801.3k TAKEOVER` headline; deferring keeps the
                    pinned card honest until the conversion lands. */}
                {isFloorPending(board.floor) ? (
                  <div
                    aria-busy="true"
                    aria-label="Floor price loading"
                    className="mt-1 h-3 w-12 rounded-[2px] bg-amber-500/15 animate-pulse"
                  />
                ) : (
                  <div className="text-[12px] font-semibold text-amber-100 tabular-nums mt-0.5 truncate whitespace-nowrap">
                    {formatBoardFloor(board.floor)}
                  </div>
                )}
              </div>
              <div
                className={cn(
                  'px-1.5 py-1 rounded-[3px] border flex items-center justify-between transition-colors',
                  seatsFilled
                    ? 'bg-emerald-950/35 border-emerald-400/45'
                    : 'bg-black/40 border-red-500/20',
                )}
              >
                <span
                  className={cn(
                    'text-[8px] tracking-[0.18em] font-mono uppercase',
                    seatsFilled ? 'text-emerald-300/80' : 'text-zinc-500',
                  )}
                >
                  Seats
                </span>
                <span
                  className={cn(
                    'text-[12px] font-semibold tabular-nums',
                    seatsFilled ? 'text-emerald-300' : 'text-zinc-100',
                  )}
                >
                  {board.taken}/{board.capacity}
                </span>
              </div>
            </div>

            {active && (
              <span
                aria-hidden
                className="absolute left-0 top-2 bottom-2 w-[2px] rounded-full bg-gradient-to-b from-red-300 via-amber-300 to-red-500"
              />
            )}
            {pending && (
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-[5px] bg-black/55 backdrop-blur-[1px]"
              >
                <Loader2 className="h-4 w-4 animate-spin text-amber-200" />
              </span>
            )}
          </button>
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        align="start"
        sideOffset={10}
        collisionPadding={12}
        className="z-[200] max-w-none border-0 bg-transparent p-0 text-left shadow-none"
      >
        <BoardTooltip board={board} />
      </TooltipContent>
    </Tooltip>
  );
}
